import WithLoadingErrorWrapper from '@/components/with-loading-error-wrapper';
import { useGetCurrentUserQuery } from '@/state/user/user-api';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import { useCallback, useEffect, type FC } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Mixpanel } from '@/lib/mixpanel';
import { useGetUserOrganizationsQuery } from '@/state';
import Session from 'supertokens-auth-react/recipe/session';
import { hardResetStore, type AppDispatch } from '@/state';
import { useDispatch } from 'react-redux';
import { useClientSideCache } from '@/hooks/use-client-side-cache';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useOrgRegisterGate, usePageTrack, useTenant, useSocketNotifications } from '@/hooks';

import { TopBar, SideBar, TopLoadingBar } from '@/components';
import UpdateProfile from '@/components/profile/update-profile';
import { OrganizationSelectorGate } from '@/guards';
import { Toaster } from '@/components/ui/sonner';

export const LayoutBackground = () => {
  return (
    <div className="absolute inset-0 fixed z-[-5]">
      {/* Primary gradient blob */}
      <div className="absolute top-0 -left-4 w-50 h-50 rounded-full filter blur-2xl opacity-70 bg-gradient-to-r from-purple-400 to-pink-400 dark:from-purple-800 dark:to-pink-800"></div>

      {/* Secondary gradient blob */}
      <div className="absolute top-0 -right-4 w-45 h-45 rounded-full filter blur-2xl opacity-70 bg-gradient-to-r from-blue-400 to-indigo-400 dark:from-white-600 dark:to-indigo-600"></div>

      {/* Tertiary gradient blob */}
      <div className="absolute -bottom-8 left-20 w-50 h-50 rounded-full filter blur-2xl opacity-70 bg-gradient-to-r from-cyan-400 to-teal-400 dark:from-cyan-600 dark:to-teal-600"></div>
      <div className="absolute bottom-1/4 right-1/4 w-32 h-32 rounded-full filter blur-2xl opacity-40 bg-gradient-to-r from-green-400 to-emerald-400 dark:from-green-500 dark:to-emerald-500"></div>
    </div>
  );
};

export const Layout: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const dispatch = useDispatch<AppDispatch>();
  const { setCurrentTenant } = useClientSideCache();

  const {
    data: currentUser,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCurrentUserQuery({
    tenant: tenant,
  }, { skip: !tenant });

  const {
    data: currentUserOrganizations,
    isLoading: isLoadingOrganizations,
    isFetching: isFetchingOrganizations,
    refetch: refetchOrganizations,
    error: organizationError,
  } = useGetUserOrganizationsQuery();

  /**
   * Global Real-time Notifications
   */
  useSocketNotifications(currentUser?.id);

  /**
   * Track page view
   */
  usePageTrack(`${location.pathname}${location.search}`, {
    path: location.pathname,
    search: location.search,
  });

  useOrgRegisterGate(isLoadingOrganizations, currentUserOrganizations);

  // Track sign in and sign up (register event only once per session, no registration on page refresh)
  useEffect(() => {
    const hasTrackedKey = 'mixpanel:loginTracked';

    if (
      currentUser &&
      !isLoading &&
      !isFetching &&
      !error &&
      !sessionStorage.getItem(hasTrackedKey)
    ) {
      Mixpanel.identify(currentUser.id);

      if (!currentUser.profile || !currentUser.profile.profile_setup) {
        Mixpanel.track('New user(First login/signup)', {
          date: new Date().toISOString(),
        });
      } else {
        Mixpanel.track('Sign In', { date: new Date().toISOString() });
      }

      sessionStorage.setItem(hasTrackedKey, 'true');
    }
  }, [currentUser, isLoading, isFetching, error]);

  const selectOrganization = useCallback(
    (selectedTenantName: string) => {
      setCurrentTenant(selectedTenantName); // Cache the tenant
      navigate(`/${selectedTenantName}/dashboard`, { replace: true });
    },
    [navigate, setCurrentTenant]
  );

  const signOut = useCallback(async () => {
    try {
      await Session.signOut(); // Ends the session and clears cookies
      await hardResetStore(dispatch);

      navigate('/auth', { replace: true }); // Redirect to login/auth page after logout
    } catch (error) {
      console.error('Logout failed', error);
    }
  }, [dispatch, navigate]);

  /**
   * Show organization selector gate
   */
  const tenantFromPath = location.pathname.split('/').filter(Boolean)[0];
  const hasTenantInPath =
    !!tenantFromPath && tenantFromPath !== 'organization' && tenantFromPath !== 'auth';
  const shouldSelectOrganization = !!(
    currentUserOrganizations?.length &&
    currentUserOrganizations.length > 0 &&
    !hasTenantInPath &&
    location.pathname === '/'
  );
  const shouldForceProfileSetup = !!(
    tenant &&
    currentUser?.profile &&
    currentUser.profile.profile_setup === false
  );

  return (
    <OrganizationSelectorGate
      show={shouldSelectOrganization}
      loading={isLoadingOrganizations}
      isFetching={isFetchingOrganizations}
      error={organizationError as ApiErrorResponse}
      onRetry={refetchOrganizations}
      organizations={currentUserOrganizations ?? []}
      onPick={(t) => selectOrganization(t)}
      onCreate={() => navigate('/organization/register')}
      onSignOut={signOut}
    >
      {/* normal Layout render */}
      <TopLoadingBar />
      <Toaster />
      <LayoutBackground />
      <WithLoadingErrorWrapper
        loading={isLoading}
        error={error as ApiErrorResponse}
        isFetching={isFetching}
        onRetry={refetch}
      >
        <UpdateProfile
          isOpen={shouldForceProfileSetup}
          onClose={() => { }}
          user={currentUser}
          isDismissable={false}
        />
        <SidebarProvider defaultOpen>
          <SideBar
            onLogout={signOut}
            userOrganizations={currentUserOrganizations}
          />
          <SidebarInset className="bg-transparent rounded-none shadow-none w-full">
            {
              tenant !== "public" && (
                <TopBar />
              ) 
            }
            <div className="p-8">
              <Outlet />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </WithLoadingErrorWrapper>
    </OrganizationSelectorGate>
  );
};
